import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { config } from "../../../Environment";
import useAuth from "../../../hooks/useAuth";
import LoggerContext from "../../../context/LoggerProvider";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { toast } from "sonner";
import BuyServiceModal from "../modals/buyService";
import useProcess from "../../../hooks/useProcess";
import ProductCard from "../ProductCard";
import { Skeleton } from "../../ui/skeleton";
import { useProductSearch } from "../../../hooks/useProductSearch";
import { Sparkles } from "lucide-react";

interface FiltersType {
  search: string;
  categories: string[];
  tags: string[];
  difficulty: string[];
  estimatedTimeMin: number | undefined;
  estimatedTimeMax: number | undefined;
  priceMin: number | undefined;
  priceMax: number | undefined;
  vendorId: string | undefined;
}

function Services({ vendorId, filters, ragResults, ragLoading }: { vendorId?: string; filters: FiltersType; ragResults?: any[] | null; ragLoading?: boolean }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const { startProcess } = useProcess();

  const retryFetch = useRetryFetch();

  const { data: serviceProducts = [], isLoading: loading } = useProductSearch({
    type: "service",
    filters,
    vendorId,
  });

  const [aboutActivityId, setAboutActivityId] = useState<string>("");
  const [buyServiceModalVisible, setBuyServiceModalVisible] = useState<boolean>(false);
  const [authorInfo, setAuthorInfo] = useState<any>(null);
  const [productInfo, setProductInfo] = useState<any>(null);
  const [activityInfo, setActivityInfo] = useState<any>(null);

  async function loadPrescriptionMetadata(id: string) {
    if (id) {
      const activityMetadataResponse = await retryFetch(
        `${config.API_BASE}/api/prescriptions/${id}/metadata`,
        {
          credentials: "include",
        }
      );
      if (activityMetadataResponse.ok) {
        const activityMetadata = await activityMetadataResponse.json();
        setActivityInfo(activityMetadata);
        setAuthorInfo(activityMetadata.fiUser);
      }
    }
  }

  async function viewServiceInfo(product: any) {
    setAboutActivityId(product._id);
    setProductInfo(product);
    await loadPrescriptionMetadata(product.data.service.fiWorkflow);
    setBuyServiceModalVisible(true);
  }

  async function buyService(product: any) {
    setAboutActivityId(product._id);
    setProductInfo(product);
    const prescriptionId = product?.data?.service?.fiWorkflow;
    if (prescriptionId) {
      await startProcess({
        prescriptionId,
        onError: () => {
          toast.error("An error occurred while starting the process.");
        },
      });
    } else {
      toast.error("An error occurred.");
    }
  }

  // Determine which data source to use
  const isRagMode = ragResults !== null && ragResults !== undefined;
  const displayProducts = isRagMode ? ragResults : serviceProducts;
  const displayLoading = isRagMode ? !!ragLoading : loading;

  return (
    <div>
      {/* RAG results indicator */}
      {isRagMode && !displayLoading && displayProducts.length > 0 && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-primary/5 border border-primary/10 rounded-lg">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm text-primary/80 font-medium">
            {t("ai_search_results") || "Showing AI-powered results"}
          </span>
          <span className="text-xs text-gray-500 ms-auto">
            {displayProducts.length} {t("results") || "results"}
          </span>
        </div>
      )}

      {/* Loading State */}
      {displayLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {Array(8)
            .fill(0)
            .map((_, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                {/* Image skeleton */}
                <Skeleton className="h-40 w-full rounded-none bg-gray-200" />

                <div className="p-5">
                  {/* Title skeleton */}
                  <Skeleton className="h-5 w-3/4 rounded bg-gray-200 mb-2" />

                  {/* Vendor name skeleton */}
                  <Skeleton className="h-4 w-1/2 rounded bg-gray-200 mb-2" />

                  {/* Categories skeleton */}
                  <div className="flex gap-1.5 mb-2">
                    <Skeleton className="h-5 w-16 rounded-full bg-gray-200" />
                    <Skeleton className="h-5 w-20 rounded-full bg-gray-200" />
                  </div>

                  {/* Description skeleton */}
                  <Skeleton className="h-4 w-full rounded bg-gray-200 mb-1" />
                  <Skeleton className="h-4 w-5/6 rounded bg-gray-200 mb-3" />

                  {/* Price skeleton */}
                  <Skeleton className="h-6 w-24 rounded bg-gray-200" />
                </div>

                {/* Footer skeleton */}
                <div className="p-4 bg-gray-50 flex gap-2 mt-auto">
                  <Skeleton className="h-9 w-full rounded bg-gray-200" />
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Empty State */}
      {!displayLoading && displayProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <p className="text-gray-600 font-medium mb-1">
            {isRagMode
              ? t("no_ai_results") || "No matching results found"
              : filters.search || filters.categories.length > 0 || filters.tags.length > 0
              ? t("no_products_found") || "No products found"
              : t("marketplace_unavailable") || "No products available"}
          </p>
          <p className="text-gray-500 text-sm">
            {isRagMode
              ? t("try_different_query") || "Try a different search query"
              : filters.search || filters.categories.length > 0 || filters.tags.length > 0
              ? t("try_different_filters") || "Try adjusting your filters"
              : t("check_back_later") || "Check back later for new products"}
          </p>
        </div>
      )}

      {/* Product Grid */}
      {!displayLoading && displayProducts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {displayProducts.map((product) => (
            <ProductCard
              key={product._id}
              product={product}
            />
          ))}
        </div>
      )}

      {/* Buy Service Modal */}
      {buyServiceModalVisible && (
        <BuyServiceModal
          productInfo={productInfo}
          buyServiceModalVisible={buyServiceModalVisible}
          setBuyServiceModalVisible={setBuyServiceModalVisible}
          activityMetadata={activityInfo}
          authorInfo={authorInfo}
        />
      )}
    </div>
  );
}

export default Services;
